/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { createContext, ReactNode, useContext } from "react";

import { MediaType } from "../../shared/types";

type PlayerSettings = {
  playbackRate: {
    defaultRate: number;
    rates: number[];
  };
  continueWatching: {
    syncIntervalMs: number;
    resumeSupportingMedia: MediaType[];
  };
  nextEpisode: {
    thresholdSeconds: number;
  };
  ui: {
    seekJumpSeconds: number;
    overlayVisibleMs: number;
    tooltipOffset: number;
  };
};

type PlayerSettingsProviderProps = {
  children: ReactNode;
};

type PlayerSettingsContextValue = PlayerSettings;

const PlayerSettingsContext = createContext<PlayerSettingsContextValue>(
  {} as PlayerSettingsContextValue,
);

export const PlayerSettingsProvider = (props: PlayerSettingsProviderProps) => {
  const { children } = props;

  const settings: PlayerSettings = {
    playbackRate: {
      defaultRate: 1,
      rates: [0.5, 0.75, 1, 1.5, 2],
    },
    continueWatching: {
      syncIntervalMs: 60 * 1000,
      resumeSupportingMedia: [
        MediaType.Video,
        MediaType.Episode,
        MediaType.Program,
        MediaType.Premiere,
        MediaType.Podcast,
        MediaType.Album,
        MediaType.Series,
      ],
    },
    nextEpisode: {
      thresholdSeconds: 10,
    },
    ui: {
      seekJumpSeconds: 10,
      overlayVisibleMs: 3 * 1000,
      tooltipOffset: -85,
    },
  };

  return (
    <PlayerSettingsContext.Provider value={{ ...settings }}>
      {children}
    </PlayerSettingsContext.Provider>
  );
};

export const usePlayerSettings = (): PlayerSettingsContextValue => {
  const context = useContext(PlayerSettingsContext);

  if (!context) {
    throw new Error("Component beyond PlayerSettingsContext");
  }

  return context;
};
